/**
 * HTML Sanitization utility using DOMPurify
 * Provides secure HTML sanitization to prevent XSS attacks
 */

// Lazy-loaded DOMPurify instance
let domPurifyInstance: typeof import('dompurify').default | null = null;

// Safe DOMPurify getter that handles SSR
const getDOMPurify = () => {
  if (typeof window === 'undefined') {
    // Server-side - return null
    return null;
  }
  
  // Client-side - use lazy loading
  if (!domPurifyInstance) {
    try {
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const DOMPurify = require('dompurify');
      domPurifyInstance = DOMPurify.default || DOMPurify;
    } catch (error) {
      console.warn('DOMPurify not available:', error);
      return null;
    }
  }
  
  return domPurifyInstance;
};

// Configuration for different content types
const SANITIZE_CONFIG = {
  // For rich text content (allows most HTML but removes dangerous elements)
  RICH_TEXT: {
    ALLOWED_TAGS: [
      'p', 'br', 'strong', 'em', 'u', 'i', 'b', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'span', 'div'
    ],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'id'],
    ALLOWED_URI_REGEXP: /^(?:(?:https?|ftp):\/\/|\/|#)/i,
  },
  
  // For basic text with minimal formatting
  BASIC_TEXT: {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'i', 'b', 'span'],
    ALLOWED_ATTR: ['class'],
  },
  
  // For titles and headings (strip all HTML)
  PLAIN_TEXT: {
    ALLOWED_TAGS: [],
    ALLOWED_ATTR: [],
  }
};

/**
 * Sanitize HTML content for rich text areas
 * Allows most formatting but removes dangerous elements and attributes
 */
export function sanitizeRichText(html: string): string {
  if (typeof html !== 'string') return '';
  
  const DOMPurify = getDOMPurify();
  if (!DOMPurify) {
    // Server-side fallback - return the original string (should be safe in SSR context)
    return html;
  }
  
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: SANITIZE_CONFIG.RICH_TEXT.ALLOWED_TAGS,
    ALLOWED_ATTR: SANITIZE_CONFIG.RICH_TEXT.ALLOWED_ATTR,
    ALLOWED_URI_REGEXP: SANITIZE_CONFIG.RICH_TEXT.ALLOWED_URI_REGEXP,
    KEEP_CONTENT: true,
    RETURN_DOM_FRAGMENT: false,
    RETURN_DOM: false,
  });
}

/**
 * Sanitize HTML content for basic formatting
 * Allows only basic text formatting tags
 */
export function sanitizeBasicHtml(html: string): string {
  if (typeof html !== 'string') return '';
  
  const DOMPurify = getDOMPurify();
  if (!DOMPurify) {
    return html;
  }
  
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: SANITIZE_CONFIG.BASIC_TEXT.ALLOWED_TAGS,
    ALLOWED_ATTR: SANITIZE_CONFIG.BASIC_TEXT.ALLOWED_ATTR,
    KEEP_CONTENT: true,
  });
}

/**
 * Strip all HTML tags and return plain text
 * Use for titles, headings, and other content that should not contain HTML
 */
export function sanitizeToPlainText(html: string): string {
  if (typeof html !== 'string') return '';
  
  const DOMPurify = getDOMPurify();
  if (!DOMPurify) {
    // Server-side fallback - strip HTML tags manually
    return html.replace(/<[^>]*>/g, '');
  }
  
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: SANITIZE_CONFIG.PLAIN_TEXT.ALLOWED_TAGS,
    ALLOWED_ATTR: SANITIZE_CONFIG.PLAIN_TEXT.ALLOWED_ATTR,
    KEEP_CONTENT: true,
  });
}

/**
 * Sanitize SVG content specifically
 * Removes script tags and dangerous attributes from SVG
 */
export function sanitizeSvg(svg: string): string {
  if (typeof svg !== 'string') return '';
  
  const DOMPurify = getDOMPurify();
  if (!DOMPurify) {
    // Server-side fallback - basic SVG cleaning
    return svg.replace(/<script[^>]*>.*?<\/script>/gi, '')
             .replace(/on\w+="[^"]*"/gi, '')
             .replace(/javascript:/gi, '');
  }
  
  return DOMPurify.sanitize(svg, {
    USE_PROFILES: { svg: true, svgFilters: true },
    KEEP_CONTENT: false,
  });
}

/**
 * Generic sanitizer that auto-detects content type and applies appropriate sanitization
 */
export function sanitizeHtml(html: string, contentType: 'rich' | 'basic' | 'plain' | 'svg' = 'rich'): string {
  switch (contentType) {
    case 'basic':
      return sanitizeBasicHtml(html);
    case 'plain':
      return sanitizeToPlainText(html);
    case 'svg':
      return sanitizeSvg(html);
    case 'rich':
    default:
      return sanitizeRichText(html);
  }
}
